import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { csrfFetch } from '../utils/csrfFetch';
import { BOOTSTRAP_MODEL_ID } from '../hooks/useModelCatalog';

/**
 * ConversationContext - Manages active conversation state
 */

const ConversationContext = createContext(null);

// Local storage key for persisting unread counts
const UNREAD_STORAGE_KEY = 'decisiv-performance-unread';

export function ConversationProvider({ children }) {
  // Current active conversation
  const [currentConversation, setCurrentConversation] = useState(null);

  // List of conversations for sidebar
  const [conversations, setConversations] = useState([]);

  // Messages in current conversation
  const [messages, setMessages] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Refresh counter - increments when conversations should be reloaded
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Unread message counts per conversation (SAT-100)
  const [unreadCounts, setUnreadCounts] = useState(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(UNREAD_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Persist unread counts to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify(unreadCounts));
    } catch {
      // Ignore storage errors
    }
  }, [unreadCounts]);

  // Calculate total unread count for tab badge
  const totalUnread = useMemo(() => {
    return Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
  }, [unreadCounts]);

  // Update document title with unread count (SAT-100)
  useEffect(() => {
    const baseTitle = 'DP Chat Interface';
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [totalUnread]);

  // Mark a conversation as read
  const markAsRead = useCallback((conversationId) => {
    if (!conversationId) return;
    setUnreadCounts(prev => {
      if (!prev[conversationId]) return prev;
      const { [conversationId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  // Increment unread count for a conversation
  const incrementUnread = useCallback((conversationId, count = 1) => {
    if (!conversationId) return;
    setUnreadCounts(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || 0) + count
    }));
  }, []);

  // Get unread count for a specific conversation
  const getUnreadCount = useCallback((conversationId) => {
    return unreadCounts[conversationId] || 0;
  }, [unreadCounts]);

  // Clear all unread counts
  const clearAllUnread = useCallback(() => {
    setUnreadCounts({});
  }, []);

  // Select a conversation
  const selectConversation = useCallback(async (conversationId) => {
    if (!conversationId) {
      setCurrentConversation(null);
      setMessages([]);
      return;
    }

    // Mark conversation as read when selected (SAT-100)
    markAsRead(conversationId);

    try {
      setLoading(true);

      // Fetch conversation details
      const response = await csrfFetch(`/api/conversations/${conversationId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentConversation(data.conversation);
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setLoading(false);
    }
  }, [markAsRead]);

  // Create new conversation
  const createConversation = useCallback(async (title = 'New Conversation', model = BOOTSTRAP_MODEL_ID) => {
    try {
      setLoading(true);

      const response = await csrfFetch('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title, model })
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentConversation(data.conversation);
        setMessages([]);
        setConversations(prev => [data.conversation, ...prev]);
        return data.conversation;
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  // Add message to current conversation
  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  // Update message in current conversation
  const updateMessage = useCallback((messageId, updates) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, ...updates } : msg
    ));
  }, []);

  // Clear current conversation
  const clearConversation = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
  }, []);

  // Fetch all conversations for sidebar
  const fetchConversations = useCallback(async () => {
    try {
      const response = await csrfFetch('/api/conversations', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || {});
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
    // Always increment to trigger sidebar refresh
    setRefreshCounter(prev => prev + 1);
  }, []);

  const value = {
    // State
    currentConversation,
    conversations,
    messages,
    loading,
    sending,
    refreshCounter,

    // Unread state (SAT-100)
    unreadCounts,
    totalUnread,

    // Actions
    selectConversation,
    createConversation,
    addMessage,
    updateMessage,
    clearConversation,
    fetchConversations,
    setConversations,
    setSending,

    // Unread actions (SAT-100)
    markAsRead,
    incrementUnread,
    getUnreadCount,
    clearAllUnread
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversation() {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
}

export default ConversationContext;
